import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, X, Loader2, CheckCircle2, ReceiptText, Users, Wallet, HandCoins, Plus } from 'lucide-react';
import { api, getApiErrorMessage } from '../api/client';
import ReceiptViewer from '../components/expenses/ReceiptViewer';
import ExpenseCostSummary from '../components/expenses/ExpenseCostSummary';
import ReimbursementReportPanel from '../components/expenses/ReimbursementReportPanel';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import TicketSelect from '../components/expenses/TicketSelect';
import { PageHeaderBand } from '../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../components/ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../lib/load-failure';
import { useExpensePermissions } from '../hooks/useExpensePermissions';
import { useAuth } from '../contexts/AuthContext';
import { displayValue } from '../features/fieldiq-handoff/contracts';
import { formatCalendarDate, instantTenantDay } from '../lib/calendar-date';
import { fmtCurrency } from '../lib/display-labels';
import {
  EXPENSE_STATUSES,
  EXPENSE_STATUS_LABELS,
  PAID_BY_LABELS,
  PAID_BY_VALUES,
  getExpenseCategoryLabel,
  getPaidByLabel,
  getPayrollPathLabel,
  toPaidBy,
  parseApproveResult,
  parseExpenseList,
  type ApproveResult,
  type Expense,
  type ExpenseStatus,
  type PaidBy,
  type PayPeriodRef,
  reimbursementPathLabel,
} from '../lib/expenses';

export const EXPENSES_QUERY_KEY = ['expenses'] as const;

/** Receipt and period dates are calendar days. @see formatCalendarDate. */
const fmtDay = (d: string | null | undefined) => formatCalendarDate(d, { month: 'short', day: 'numeric' }) ?? '—';

export function fmtPayPeriod(p: PayPeriodRef | null): string {
  if (!p || (!p.start && !p.end)) return '—';
  return `${fmtDay(p.start)} – ${fmtDay(p.end)}`;
}

/**
 * The office wants reimbursements and company costs APART, so the two lists
 * are asked for separately (0372). `'all'` is not a value the API takes — it
 * is the absent param, which is what every caller written before that column
 * sends and what returns both.
 */
type PaidByFilter = 'all' | PaidBy;

const PAID_BY_FILTERS: readonly PaidByFilter[] = ['all', ...PAID_BY_VALUES];

const PAID_BY_FILTER_LABELS: Record<PaidByFilter, string> = {
  all: 'All',
  ...PAID_BY_LABELS,
};

/**
 * The register's status pill and row rail: dark ink on a saturated signal
 * fill, the Quotes / Rate Sheets / Equipment / Invoices badge.
 *
 * EXHAUSTIVE over `expenses_status_check`, which has exactly three members —
 * pending, approved, rejected (migration 0363). Three statuses, five signal
 * hues, so none is shared: pending is amber (waiting on this office),
 * approved green, rejected red.
 *
 * A value outside the CHECK reads `—` on slate with a transparent rail. It
 * must never fall back to a real status: on an approvals register "Approved"
 * is a claim that money is on its way to someone.
 *
 * THE UNKNOWN BRANCH IS CURRENTLY UNREACHABLE FROM THE LIST, AND IS KEPT
 * ANYWAY. `rows` below narrows to `e.status === tab`, so a row whose status
 * the screen cannot classify is dropped before it is drawn. That filter is
 * the thing that could change; the no-guessing rule should not have to be
 * rediscovered when it does.
 */
const STATUS_BADGE: Record<string, { label: string; fill: string; rail: string }> = {
  pending: { label: 'Pending', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  approved: { label: 'Approved', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  rejected: { label: 'Rejected', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};

const UNKNOWN_STATUS = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };

export const statusSkin = (status: string | null | undefined) =>
  (status && Object.prototype.hasOwnProperty.call(STATUS_BADGE, status) && STATUS_BADGE[status]) || UNKNOWN_STATUS;

function ExpenseStatusBadge({ status }: { status: string | null | undefined }) {
  const skin = statusSkin(status);
  return (
    <span
      data-testid="expense-status-badge"
      className={`inline-flex w-fit items-center rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

interface LastApproval {
  expense: Expense | null;
  fallbackVendor: string | null;
  result: ApproveResult;
}

/**
 * The sentence the office reads after Approve. The number they will be asked
 * about is WHICH pay period the reimbursement landed on — "why isn't it on
 * this cheque" is the whole question — so a carry-forward is named outright,
 * with the period it went to. When the API did not say, the banner says that,
 * rather than guessing a period from today's date.
 *
 * A COMPANY-PAID APPROVAL IS A DIFFERENT SENTENCE (0372). No adjustment is
 * written, so there is no period, and "the API did not report which pay period
 * it landed on" would report a correct result as a defect. The API states
 * `payroll_path` for exactly this reason and the banner prints it.
 */
export function approvalSentence(a: LastApproval): {
  headline: string;
  detail: string | null;
  note: string | null;
  ticketNote: string | null;
  carried: boolean;
  /** `'reimbursement'` | `'none'` | null — the API's word, never derived. */
  payrollPath: string | null;
  payrollPathLabel: string;
} {
  const exp = a.result.expense ?? a.expense;
  const who = exp?.workerName ? ` for ${exp.workerName}` : '';
  const vendor = exp?.vendor ?? a.fallbackVendor ?? 'Expense';
  const headline = `Approved ${vendor} · ${fmtCurrency(exp?.amount)}${who}`;
  // WHICH BRANCH RAN, stated by the API. A company-paid approval writes no
  // adjustment and comes back with a null period, a null adjustment and a null
  // reflected flag — the same three nulls a FAILURE would produce. The word is
  // read, never inferred from them.
  const payrollPath = a.result.payrollPath;
  // The API's own sentence about the period total, shown whenever it sent one.
  // It is NOT gated on `reflected === false` any more: a company-paid row's
  // reflected flag is null, and that gate would have swallowed the one line
  // that says the null means "never reaches payroll" rather than "failed".
  const note =
    a.result.periodTotalNote
    ?? (a.result.reflectedInPeriodTotal === false
      ? 'Not yet in the period total — it lands on the next Generate + Save of that period.'
      : null);
  // A ticket sent with a re-approve is NOT applied — the expense is past
  // pending and the write branch never runs. The API reports that rather than
  // dropping it silently, and the office has to be told, or it comes to
  // believe a ticket is attached when it is not.
  const ticketNote =
    a.result.ticketIdApplied === false
      ? (a.result.ticketIdNote ?? 'The ticket was NOT changed — this expense is already approved.')
      : null;
  const period = a.result.payPeriod;
  // The API's sentence names the period it came FROM as well as the one it
  // went to, so it wins whenever it is there.
  const path = { payrollPath, payrollPathLabel: getPayrollPathLabel(payrollPath) };
  if (a.result.message) return { headline, detail: a.result.message, note, ticketNote, carried: a.result.carriedForward, ...path };
  if (!period || (!period.start && !period.end && !period.id)) {
    // A company-paid approval has no pay period BY DESIGN — saying the API
    // failed to report one would report a correct result as a defect.
    return {
      headline,
      detail: payrollPath === 'none' ? null : 'The API did not report which pay period it landed on.',
      note, ticketNote, carried: false, ...path,
    };
  }
  const where = fmtPayPeriod(period) === '—' ? `pay period ${period.id}` : `pay period ${fmtPayPeriod(period)}`;
  if (a.result.carriedForward) {
    return {
      headline,
      detail: `Carried forward: the receipt's own period was already closed, so the reimbursement is on ${where}. That is the cheque it will show up on.`,
      note,
      ticketNote,
      carried: true,
      ...path,
    };
  }
  return { headline, detail: `Reimbursement on ${where}.`, note, ticketNote, carried: false, ...path };
}

function RejectModal({
  expense,
  isLoading,
  error,
  onClose,
  onConfirm,
}: {
  expense: Expense;
  isLoading: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const canConfirm = reason.trim().length > 0 && !isLoading;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-label="Reject expense"
        className="bg-fieldiq-card border border-fieldiq-border rounded-2xl p-6 w-full max-w-md shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold text-foreground">Reject this receipt?</h3>
          <button onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-white/10 text-muted-foreground">
            <X size={18} />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          {expense.workerName ?? 'Worker'} · {expense.vendor ?? '—'} · {fmtCurrency(expense.amount)}
        </p>
        <label className="text-xs text-muted-foreground mb-1 block" htmlFor="reject-reason">
          Reason — the hand sees this
        </label>
        <textarea
          id="reject-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          autoFocus
          placeholder="e.g. Personal purchase, not a job expense"
          className="w-full bg-background rounded-lg px-3 py-2.5 text-sm border border-border focus:border-fieldiq-accent outline-none text-foreground resize-none mb-3"
        />
        {error && <p className="text-xs text-fieldiq-danger mb-3">{error}</p>}
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-white/5">
            Cancel
          </button>
          <button
            onClick={() => canConfirm && onConfirm(reason.trim())}
            disabled={!canConfirm}
            className="flex-1 py-2.5 rounded-lg text-sm font-bold bg-red-600 text-white disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {isLoading && <Loader2 size={14} className="animate-spin" />}
            Confirm rejection
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ExpenseApprovalScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<ExpenseStatus>('pending');
  const [paidByFilter, setPaidByFilter] = useState<PaidByFilter>('all');
  /**
   * FE-1: "Mine" — the receipts I filed. Only an office VIEW role needs the
   * choice: GET /expenses already narrows everyone else (logistics, supervisor)
   * to their own rows, so for them the whole register IS "mine" and a toggle
   * would offer a filter that changes nothing. `?mine=1` is where a filed
   * receipt lands, so the filer sees it straight away.
   */
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [mine, setMine] = useState<boolean>(searchParams.get('mine') === '1');
  /**
   * FE-2: one named person's receipts (office only). Mutually exclusive with
   * "Mine" — both are the same user_id filter on the API, and two controls
   * holding two different people would send whichever was set last.
   */
  const [personId, setPersonId] = useState<string>('');
  /**
   * FE-2: Job vs General (0501). Filtered HERE, over the list already loaded,
   * and NOT sent as expense_kind: GET /expenses is unpaginated (no LIMIT, no
   * OFFSET), so the per-kind counts on these chips are exact tallies of the
   * rows fetched for the status and paid-by shown — which is only true while
   * the server returns both kinds. A row with no kind in the payload is not
   * guessed into either; it shows under All only.
   */
  const [kindFilter, setKindFilter] = useState<'all' | 'job' | 'general'>('all');
  /** FE-5: the register, or the monthly reimbursement report. */
  const [view, setView] = useState<'receipts' | 'report'>(searchParams.get('view') === 'report' ? 'report' : 'receipts');
  /**
   * FE-5 (D13): a refused self-approval, shown IN PLACE on its row, by id. The
   * screen does not hide Approve on your own row — the sole-active-admin
   * exception depends on an admin count it does not have, so it lets the API
   * decide and says what the API said.
   */
  const [approveErrors, setApproveErrors] = useState<Record<string, string>>({});
  const tenantZone = useTenantTimeZone();
  const [rejectTarget, setRejectTarget] = useState<Expense | null>(null);
  const [lastApproval, setLastApproval] = useState<LastApproval | null>(null);
  /**
   * Per-row ticket edits, by expense id. A row with no entry has not been
   * touched and its stored ticket stands — which is why this is a "key
   * present?" test and not `?? e.ticketId`: clearing a ticket to none stores
   * `null`, and a nullish fallback would read that as "unedited".
   */
  const [ticketEdits, setTicketEdits] = useState<Record<string, string | null>>({});
  const editedTicket = (e: Expense): string | null =>
    Object.prototype.hasOwnProperty.call(ticketEdits, e.id) ? ticketEdits[e.id] : e.ticketId;

  /**
   * Which controls this reader gets, PER ACTION — see useExpensePermissions.
   * Approve and Reject are `requireRole('admin', 'office_staff')`; correcting
   * who paid is a PATCH with no role gate at all. A plain dispatcher gets one
   * and not the others, so there is no single answer for the screen.
   */
  const perms = useExpensePermissions();
  // canCorrectPaidBy IS the office-view test (OFFICE_VIEW_ROLES) — see the hook.
  const officeView = perms.canCorrectPaidBy;
  const mineUserId = officeView && mine && user?.id ? String(user.id) : null;
  const filterUserId = mineUserId ?? (officeView && personId ? personId : null);
  // Who can be picked: the tenant's people, for the office view roles only.
  const peopleQuery = useQuery({
    queryKey: ['expense-people'],
    queryFn: () =>
      api.users.list().then((r) => {
        const body = r.data as any;
        const list = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
        return list
          .filter((u: any) => u && u.id && u.role !== 'customer')
          .map((u: any) => ({ id: String(u.id), name: String(u.fullName ?? [u.firstName, u.lastName].filter(Boolean).join(' ') ?? u.email ?? u.id) }))
          .sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name));
      }),
    enabled: officeView,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  const listQuery = useQuery({
    queryKey: [...EXPENSES_QUERY_KEY, tab, paidByFilter, filterUserId],
    // The paid_by key is OMITTED on 'all' rather than sent empty: absent is
    // what returns both lists, and the API 400s on anything outside the pair.
    // user_id likewise (0501): only sent for "Mine", and the API honours it
    // only for an office view role.
    queryFn: () =>
      api.expenses
        .list({
          status: tab,
          ...(paidByFilter === 'all' ? {} : { paid_by: paidByFilter }),
          ...(filterUserId ? { user_id: filterUserId } : {}),
        })
        .then((r) => parseExpenseList(r.data)),
    // A refusal is an answer, not a blip, and a 5xx that is waiting to retry
    // must land in `error` rather than pausing into an empty list. Both are
    // LOAD_FAILURE_QUERY_OPTIONS' job; see lib/load-failure.ts.
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const { data, isLoading } = listQuery;
  /**
   * THE THIRD STATE. Read off the QUERY, not off `error` alone: a query that
   * failed and is between attempts holds its failure in `failureReason` with
   * `error` still null, and every branch keyed on `error` then renders the
   * empty state over a load that did not arrive (fe#705).
   */
  const failure = queryLoadFailure(listQuery);

  // The list endpoint is asked for one status; a row that came back with a
  // different one is still filtered here so a tab never shows the wrong state.
  //
  // paid_by is deliberately NOT re-filtered the same way. `status` is always
  // in the payload, so the guard can only ever drop a genuinely wrong row;
  // `paidBy` is null when a payload does not carry it, and narrowing on it
  // here would then drop that row from BOTH lists — a receipt vanishing
  // entirely is worse than one appearing under the wrong heading. The API's
  // WHERE clause is what separates them.
  const statusRows = useMemo(() => (data ?? []).filter((e) => e.status === tab), [data, tab]);
  const kindCounts = useMemo(() => ({
    all: statusRows.length,
    job: statusRows.filter((e) => e.expenseKind === 'job').length,
    general: statusRows.filter((e) => e.expenseKind === 'general').length,
  }), [statusRows]);
  const rows = useMemo(
    () => (kindFilter === 'all' ? statusRows : statusRows.filter((e) => e.expenseKind === kindFilter)),
    [statusRows, kindFilter],
  );

  /**
   * FE-5: WHERE APPROVING SENDS THE MONEY, said before the click. For a pending
   * PERSONAL receipt that is the person's RESOLVED reimbursement path — read
   * from GET /users/:id (`expenseReimbursementResolved`, any colleague may read
   * it), never guessed from their role or pay rates here. One request per
   * distinct person, cached. Company-paid receipts need no lookup: no money moves.
   */
  const pendingWorkers = useMemo(
    () => (tab === 'pending' && perms.canApprove
      ? Array.from(new Set(rows.filter((e) => e.paidBy === 'personal' && e.workerId).map((e) => e.workerId as string)))
      : []),
    [rows, tab, perms.canApprove],
  );
  const resolvedQueries = useQueries({
    queries: pendingWorkers.map((id) => ({
      queryKey: ['expense-approve-destination', id],
      queryFn: () => api.users.get(id).then((r) => (r.data as any)?.data ?? r.data),
      staleTime: 60_000,
    })),
  });
  const destinationOf = (e: Expense): string | null => {
    if (e.paidBy === 'company') return 'No reimbursement (company-paid)';
    const i = pendingWorkers.indexOf(e.workerId ?? '');
    const path = i >= 0 ? (resolvedQueries[i]?.data as any)?.expenseReimbursementResolved?.path : undefined;
    return path === 'payroll' ? 'Payroll' : path === 'report' ? 'Reimbursement report' : null;
  };

  /**
   * The band's four figures and the active chip's count: tallies of the ONE
   * list this screen already loads, with no added query.
   *
   * WHICH LIST, AND WHY THE OTHER CHIPS READ A DASH. GET /expenses filters by
   * status on the server and returns every matching row — no LIMIT, no OFFSET,
   * no pagination anywhere in the route — so a tally of the loaded rows IS the
   * whole count for the tab in view. It is not a count for any other tab:
   * those rows were never fetched, and no endpoint returns per-status counts.
   * `/expenses/summary` is not that endpoint — it is APPROVED-ONLY and grouped
   * by job, so it cannot answer "how many are waiting", which is the one thing
   * this screen exists to say. An unselected chip is therefore absent, not
   * zero, and the band describes the tab in view.
   *
   * `null` while the list is in flight and whenever it did not arrive;
   * displayValue() and fmtCurrency() render every one of those as a dash.
   *
   * THE TOTAL IS ALL-OR-NOTHING ON PURPOSE. A sum that silently skips the rows
   * whose amount is absent is an understatement of money that looks exactly
   * like a correct figure — the defect this file's own em-dash rule exists to
   * stop. So one unreadable amount makes the whole figure a dash, and the
   * office is told nothing rather than told less than the truth. (`[].every`
   * is `true`, which is correct here and not the fe#702 trap: the sum of an
   * empty loaded list really is $0.00, and `receipts` beside it reads 0.)
   */
  const tally = useMemo(() => {
    if (!Array.isArray(data)) return null;
    const everyAmountKnown = rows.every((e) => typeof e.amount === 'number' && Number.isFinite(e.amount));
    const named = rows.every((e) => e.workerId ?? e.workerName);
    return {
      receipts: rows.length,
      total: everyAmountKnown ? rows.reduce((sum, e) => sum + (e.amount as number), 0) : undefined,
      // Without something to tell two filers apart there is no distinct count
      // to give, so it is absent rather than a confident undercount.
      people: named ? new Set(rows.map((e) => e.workerId ?? e.workerName)).size : undefined,
      reimbursable: rows.filter((e) => e.paidBy === 'personal').length,
    };
  }, [data, rows]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: EXPENSES_QUERY_KEY });

  const approveMutation = useMutation({
    // The body is sent ONLY when the ticket actually moved. Approve has never
    // taken one, an absent key means "leave it alone", and `ticket_id: null`
    // means "clear it" — so an unchanged row must send no body at all rather
    // than restate what is already stored.
    mutationFn: ({ id, ticketId, changed }: { id: string; ticketId: string | null; changed: boolean }) =>
      api.expenses.approve(id, changed ? { ticket_id: ticketId } : undefined),
    onSuccess: (res, { id }) => {
      setApproveErrors((prev) => { const { [id]: _gone, ...rest } = prev; return rest; });
      const expense = rows.find((e) => e.id === id) ?? null;
      setLastApproval({ expense, fallbackVendor: expense?.vendor ?? null, result: parseApproveResult(res.data) });
      // The row leaves this tab; its pending edit has been spent.
      setTicketEdits((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, id)) return prev;
        const { [id]: _spent, ...rest } = prev;
        return rest;
      });
    },
    onError: (err: any, { id }) => {
      // A 409 is a REFUSAL with a reason the approver can act on (your own
      // expense; a closed period) — it belongs on the row it refused. Anything
      // else is a failure, and keeps the toast.
      if (err?.response?.status === 409) {
        setApproveErrors((prev) => ({ ...prev, [id]: getApiErrorMessage(err, 'This expense cannot be approved.') }));
        return;
      }
      toast.error(getApiErrorMessage(err, 'Could not approve this expense.'));
    },
    onSettled: invalidate,
  });

  /**
   * The office CORRECTING who paid, while the expense is still pending.
   *
   * A hand may file 'company' — they are the one holding the receipt — and the
   * office is who knows for certain. Until now the only correction was
   * rejecting the receipt outright, which sends a hand's honest paperwork back
   * over a field they were guessing at.
   *
   * THIS WRITES IMMEDIATELY, unlike the ticket edit two cells over. The ticket
   * is deferred because approve takes it in its body; `paid_by` has no such
   * ride, and a local edit that looked applied and was never sent is the exact
   * silent failure this screen exists to avoid. So: PATCH on change, the row
   * disabled while it is in flight, and the API's own row read back.
   *
   * PENDING ONLY, and the API says the same thing — every field on PATCH is
   * behind a `status = 'pending'` gate that 409s otherwise. That is the right
   * rule: once approved, either the money is on a pay period or the deliberate
   * absence of it is recorded, and flipping this would silently contradict
   * whichever one happened.
   */
  const paidByMutation = useMutation({
    // Exactly one key. PATCH refuses a body with no editable fields (400), and
    // restating the fields we did not touch would make an unrelated edit
    // possible from a control that promises one.
    mutationFn: ({ id, paidBy }: { id: string; paidBy: PaidBy; was: string | null }) =>
      api.expenses.update(id, { paid_by: paidBy }),
    onSuccess: (_res, { paidBy }) => {
      // Say the CONSEQUENCE, not the field name. And say it even though the
      // row may be about to leave the list: when a paid_by narrowing is
      // active, correcting a row moves it out of the current view on the
      // invalidate below, and a row disappearing with no word for it reads as
      // a receipt that was lost rather than one that was filed correctly.
      const leaves = paidByFilter !== 'all' && paidByFilter !== paidBy;
      toast.success(
        (paidBy === 'company'
          ? 'Marked company-paid — it will not be reimbursed.'
          : 'Marked personal — it will be reimbursed on approval.')
        + (leaves ? ` It has moved to the ${PAID_BY_FILTER_LABELS[paidBy]} list.` : ''),
      );
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not change who paid.')),
    onSettled: invalidate,
  });
  /** Which row is mid-write, so only that select is disabled. */
  const paidByBusyId = paidByMutation.isPending ? paidByMutation.variables?.id ?? null : null;

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.expenses.reject(id, { reason }),
    onSuccess: () => {
      setRejectTarget(null);
      toast.success('Rejected — the hand will see the reason.');
    },
    onSettled: invalidate,
  });

  const banner = lastApproval ? approvalSentence(lastApproval) : null;

  return (
    <div data-testid="expenses-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      {/* The app shell renders the one global header. Pages never do. */}
      <PageHeaderBand
        eyebrow="Field spend"
        title="Expenses"
        onBack={() => navigate(-1)}
        backLabel="Back"
        /* FE-1: every internal role may file their own receipt — the route is
           in the office tree, and POST /expenses has no role gate. */
        actions={
          <button
            type="button"
            data-testid="expenses-add-receipt"
            onClick={() => navigate('/expenses/new')}
            className="fi-action fi-action--primary"
          >
            <Plus className="size-4" aria-hidden="true" /> Add receipt
          </button>
        }
        /* The four figures describe the tab and paid-by narrowing in view —
           see `tally` for why they are tallies of the loaded list and why an
           unreadable amount makes the money figure a dash rather than a
           smaller number. The third label is "People" (47px): the circle's
           edge crosses the third column between 1024 and 1048, and the
           clearance was measured for labels no wider than about 70px. */
        stats={[
          {
            icon: <ReceiptText className="size-4" aria-hidden="true" />,
            label: 'Receipts',
            sub: 'In the tab shown',
            tone: 'accent',
            loading: isLoading,
            value: displayValue(tally?.receipts),
          },
          {
            icon: <Wallet className="size-4" aria-hidden="true" />,
            label: 'Total',
            sub: 'Receipts shown',
            tone: 'info',
            loading: isLoading,
            value: fmtCurrency(tally?.total),
          },
          {
            icon: <Users className="size-4" aria-hidden="true" />,
            label: 'People',
            sub: 'Who filed them',
            tone: 'neutral',
            loading: isLoading,
            value: displayValue(tally?.people),
          },
          {
            icon: <HandCoins className="size-4" aria-hidden="true" />,
            label: 'To reimburse',
            sub: 'Personal, not company',
            tone: 'success',
            loading: isLoading,
            value: displayValue(tally?.reimbursable),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/* The Locations pattern (#686): the list stays, and one line says
              who can act on it. Not a refusal notice — this reader can see
              every receipt here; they simply cannot be the one to decide it. */}
          {!perms.canApprove && (
            <p data-testid="expenses-readonly-note" className="text-xs text-fieldiq-muted">
              Only an admin or office staff can approve or reject a receipt.
            </p>
          )}

          {banner && (
            <div
              role="status"
              className={`rounded-fi-panel border px-4 py-3 text-sm flex items-start gap-2 ${
                banner.carried
                  ? 'border-fi-amber/30 bg-fi-amber/10 text-fi-signal-amber'
                  : 'border-fi-green/30 bg-fi-green/10 text-fi-signal-green'
              }`}
            >
              <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-medium">
                  {banner.headline}
                  {/* WHICH BRANCH RAN, in the API's word. 'none' is a result,
                      not an absence, and must never read as a failed
                      reimbursement. */}
                  {banner.payrollPath && (
                    <span
                      className={`ml-2 text-[11px] px-1.5 py-0.5 rounded align-middle ${
                        banner.payrollPath === 'none'
                          ? 'bg-white/10 text-fieldiq-text'
                          : 'bg-fi-green/15 text-fi-signal-green'
                      }`}
                    >
                      {banner.payrollPathLabel}
                    </span>
                  )}
                </p>
                {banner.detail && <p className="mt-0.5 opacity-90">{banner.detail}</p>}
                {banner.note && <p className="mt-0.5 text-xs opacity-75">{banner.note}</p>}
                {lastApproval?.result.selfApproved && (
                  <p data-testid="self-approved-note" className="mt-1 text-xs font-medium">
                    Self-approved (sole admin) — recorded in the audit log.
                  </p>
                )}
                {banner.ticketNote && (
                  <p className="mt-1 text-xs font-medium text-fi-signal-amber">{banner.ticketNote}</p>
                )}
              </div>
              <button onClick={() => setLastApproval(null)} aria-label="Dismiss" className="opacity-70 hover:opacity-100">
                <X size={16} />
              </button>
            </div>
          )}

          {/* FE-5: the receipts register, or the monthly reimbursement report. */}
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Expenses view">
            {(['receipts', 'report'] as const).map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={view === v}
                onClick={() => setView(v)}
                className={`rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                  view === v
                    ? 'bg-fi-amber text-fi-amber-ink'
                    : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                }`}
              >
                {v === 'receipts' ? 'Receipts' : 'Reimbursement report'}
              </button>
            ))}
          </div>

          {view === 'report' && (
            <ReimbursementReportPanel
              officeView={officeView}
              canMarkReimbursed={perms.canMarkReimbursed}
              currentUserId={user?.id ? String(user.id) : null}
              people={(peopleQuery.data ?? []) as Array<{ id: string; name: string }>}
            />
          )}

          {view === 'receipts' && (<>
          {/* Status chips, in the body rather than on the band. These are a
              STATUS FILTER over one list, not a tab strip switching views, so
              they take the Quotes / Invoices chip row — the shape settled for
              /finance/invoices' underlined "tabs" (fe#704).

              Only the selected chip carries a count, because only its rows
              were fetched; see `tally`. None is claimed while that list is in
              flight, so a chip never shows a stale number under a new filter. */}
          <div className="scrollbar-hide flex gap-2 overflow-x-auto" role="group" aria-label="Filter by status">
            {EXPENSE_STATUSES.map((s) => {
              const active = tab === s;
              return (
                <button
                  key={s}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setTab(s)}
                  className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                    active
                      ? 'bg-fi-amber text-fi-amber-ink'
                      : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                  }`}
                >
                  {EXPENSE_STATUS_LABELS[s]}
                  {!(active && isLoading) && (
                    <span
                      data-testid={`expense-chip-count-${s}`}
                      className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}
                    >
                      {displayValue(active ? tally?.receipts : undefined)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/*
            * Reimbursements and company costs, apart. This is a separate
            * question from the status chips above — the office asks both at
            * once ("which company-card receipts are still pending?") — so it
            * is its own control rather than three more chips. It carries no
            * counts: the two lists are alternatives to the one loaded, and
            * neither has been fetched.
            */}
          {officeView && (
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Whose receipts">
              <span className="text-xs text-fieldiq-muted">Showing</span>
              {([false, true] as const).map((m) => (
                <button
                  key={String(m)}
                  type="button"
                  aria-pressed={!personId && mine === m}
                  onClick={() => { setMine(m); setPersonId(''); }}
                  className={`rounded-fi-pill border px-3 py-1.5 text-xs font-bold transition-colors ${
                    !personId && mine === m
                      ? 'border-fi-amber bg-fieldiq-card text-fieldiq-text'
                      : 'border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                  }`}
                >
                  {m ? 'Mine' : 'Everyone'}
                </button>
              ))}
              <select
                aria-label="Filter by person"
                value={personId}
                onChange={(ev) => { setPersonId(ev.target.value); if (ev.target.value) setMine(false); }}
                disabled={peopleQuery.isLoading || !!queryLoadFailure(peopleQuery)}
                className="rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-2 py-1 text-xs text-fieldiq-text focus:border-fi-amber outline-none disabled:opacity-50"
              >
                <option value="">
                  {queryLoadFailure(peopleQuery) ? 'People unavailable' : 'Any person'}
                </option>
                {(peopleQuery.data ?? []).map((p: { id: string; name: string }) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* FE-2: what the receipt was for (0501). Counts are tallies of the
              loaded rows for the status and paid-by shown — exact, because
              GET /expenses is unpaginated; see `kindFilter`. */}
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by kind">
            <span className="text-xs text-fieldiq-muted">For</span>
            {(['all', 'job', 'general'] as const).map((k) => (
              <button
                key={k}
                type="button"
                aria-pressed={kindFilter === k}
                onClick={() => setKindFilter(k)}
                className={`inline-flex items-center gap-1.5 rounded-fi-pill border px-3 py-1.5 text-xs font-bold transition-colors ${
                  kindFilter === k
                    ? 'border-fi-amber bg-fieldiq-card text-fieldiq-text'
                    : 'border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                }`}
              >
                {k === 'all' ? 'All' : k === 'job' ? 'Job' : 'General'}
                {!isLoading && !failure && (
                  <span data-testid={`expense-kind-count-${k}`} className="tabular-nums">{kindCounts[k]}</span>
                )}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by who paid">
            <span className="text-xs text-fieldiq-muted">Paid by</span>
            {PAID_BY_FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                aria-pressed={paidByFilter === f}
                onClick={() => setPaidByFilter(f)}
                className={`rounded-fi-pill border px-3 py-1.5 text-xs font-bold transition-colors ${
                  paidByFilter === f
                    ? 'border-fi-amber bg-fieldiq-card text-fieldiq-text'
                    : 'border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                }`}
              >
                {PAID_BY_FILTER_LABELS[f]}
              </button>
            ))}
          </div>

          {/* The register. The amber edge is on this panel and nowhere else;
              rows carry a status rail, never a second amber one. */}
          <section
            data-testid="expense-register"
            aria-labelledby="expense-register-title"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="border-b border-fieldiq-border px-4 py-4">
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Receipt register</p>
              <h2 id="expense-register-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                {EXPENSE_STATUS_LABELS[tab]} receipts
              </h2>
            </header>

            {isLoading ? (
              <div className="flex items-center gap-2 text-fieldiq-muted py-12 justify-center">
                <Loader2 size={18} className="animate-spin" /> Loading expenses…
              </div>
            ) : failure ? (
              /* THE THIRD BRANCH, where the empty state would have been. A
                 refused list is not "no receipts": the approve and reject
                 routes are admin / office staff, so the reader who is refused
                 here is being told what they may see, not what exists. */
              <LoadFailureNotice
                error={queryFailureError(listQuery)}
                kind={failure}
                subject="expenses"
                allowed="Admins, office staff and dispatchers"
              />
            ) : rows.length === 0 ? (
              <div className="px-4 py-16 text-center">
                <ReceiptText size={40} className="mx-auto mb-3 text-fieldiq-muted" />
                <p className="text-fieldiq-text font-semibold">No {EXPENSE_STATUS_LABELS[tab].toLowerCase()} expenses</p>
                <p className="text-sm text-fieldiq-muted mt-1">
                  {tab === 'pending'
                    ? (mineUserId || !officeView ? 'Receipts you file land here.' : 'Receipts hands file from the field land here.')
                    : 'Nothing in this state yet.'}
                </p>
              </div>
            ) : (
              /* A min width rather than a card stack below md: every row here
                 carries up to three live controls, and a second copy of them
                 in a card tree would put two of each on the page. The rows
                 scroll sideways, which is what the standard asks of a table
                 this wide. */
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1180px] text-left text-sm">
                  <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                    {/* Transparent 4px so the head lines up with the railed rows. */}
                    <tr className="border-l-4 border-l-transparent">
                      <th scope="col" className="px-4 py-3">Worker</th>
                      <th scope="col" className="px-4 py-3">Job</th>
                      <th scope="col" className="px-4 py-3">Customer</th>
                      <th scope="col" className="px-4 py-3">Ticket</th>
                      <th scope="col" className="px-4 py-3">Date</th>
                      <th scope="col" className="px-4 py-3">Vendor</th>
                      <th scope="col" className="px-4 py-3">Category</th>
                      <th scope="col" className="px-4 py-3">Status</th>
                      <th scope="col" className="px-4 py-3">Paid by</th>
                      <th scope="col" className="px-4 py-3 text-right">Total</th>
                      <th scope="col" className="px-4 py-3 text-right">GST</th>
                      <th scope="col" className="px-4 py-3">Receipt</th>
                      {tab === 'approved' && <th scope="col" className="px-4 py-3">Pay period</th>}
                      {tab === 'approved' && <th scope="col" className="px-4 py-3">Payroll</th>}
                      {tab === 'rejected' && <th scope="col" className="px-4 py-3">Reason</th>}
                      {tab === 'pending' && (perms.canApprove || perms.canReject) && (
                        <th scope="col" className="px-4 py-3 text-right sticky right-0 bg-fieldiq-card">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((e) => (
                      /* NOT a row target, and deliberately not the fe#647
                         row either. That exception exists to stop a row with
                         inline buttons taking role="button"; this register
                         goes further and offers no row-level activation at
                         all, because there is no expense detail screen to
                         open. A tabIndex on a row that does nothing is a
                         focus stop that leads nowhere. */
                      <tr
                        key={e.id}
                        data-testid={`expense-row-${e.id}`}
                        className={`border-t border-l-4 border-fieldiq-border ${statusSkin(e.status).rail}`}
                      >
                        <td className="px-4 py-3 text-fieldiq-text whitespace-nowrap">{e.workerName ?? '—'}</td>
                        {/* A general receipt (0501) has no job by design — say
                            so, rather than a dash that reads as missing data. */}
                        <td className="px-4 py-3 text-fieldiq-text whitespace-nowrap">
                          {e.jobNumber ?? '—'}
                          {/* Slate, like the unknown-status badge: a fact about
                              the receipt, not a signal — no new hue. */}
                          {e.expenseKind === 'general' && (
                            <span
                              data-testid={`expense-general-badge-${e.id}`}
                              className="ml-2 inline-flex items-center rounded-fi-pill border border-black/20 bg-fi-signal-slate px-2 py-0.5 text-[0.62rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink"
                            >
                              General
                            </span>
                          )}
                        </td>
                        {/* The API's effective customer: the receipt's own, else
                            its job's. Absent is a dash, never a blank. */}
                        <td className="px-4 py-3 text-fieldiq-text whitespace-nowrap" data-testid={`expense-customer-${e.id}`}>
                          {e.customerName ?? '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {tab === 'pending' && e.jobId && perms.canApprove ? (
                            // Editable right up to the moment of approval —
                            // the office is usually the first person who knows
                            // which ticket the day's work went on. Shown only
                            // to a reader who can approve, because the ticket
                            // rides in approve's OWN body and has no other way
                            // to reach the API.
                            <TicketSelect
                              ariaLabel={`Ticket — ${e.vendor ?? 'expense'}`}
                              jobId={e.jobId}
                              value={editedTicket(e)}
                              onChange={(ticketId) => setTicketEdits((prev) => ({ ...prev, [e.id]: ticketId }))}
                              disabled={approveMutation.isPending}
                              selectClassName="rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-2 py-1 text-xs text-fieldiq-text focus:border-fi-amber outline-none max-w-[10rem]"
                              noteClassName="text-xs text-fieldiq-muted"
                            />
                          ) : (
                            <span className="text-fieldiq-text">
                              {e.ticketNumber ?? '—'}
                              {/* The read join keeps the number after a soft
                                  delete on purpose; blanking it would render an
                                  existing link as absent. Say which it is. */}
                              {e.ticketDeleted === true && (
                                <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-fi-amber/15 text-fi-signal-amber">
                                  ticket deleted
                                </span>
                              )}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-fieldiq-text whitespace-nowrap">{fmtDay(e.expenseDate)}</td>
                        {/* THE WORKER'S NOTE, RENDERED (0389). It was captured
                            from the first version of this feature and displayed
                            NOWHERE — not here, not on the hand's own list — so
                            anything typed into it was write-only.
                            That became load-bearing when a company-paid receipt
                            was allowed to be $0: TNT's fuel card prints litres
                            and no price, the litres go in this field, and
                            without it the approver sees `Shell · Fuel ·
                            Company card · $0.00` with no way to tell a real
                            fuel-card fill from a mis-filed row.
                            Under the vendor rather than in a new column: the
                            table already carries up to 14, and this is the
                            reading position the note belongs in. Wraps, so a
                            long note is not truncated into a lie. */}
                        <td className="px-4 py-3 text-fieldiq-text align-top">
                          <div>{e.vendor ?? '—'}</div>
                          {e.description && (
                            <div className="text-xs text-fieldiq-muted mt-0.5 max-w-[22rem] whitespace-pre-line break-words">
                              {e.description}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-fieldiq-text">{getExpenseCategoryLabel(e.category)}</td>
                        <td className="px-4 py-3">
                          <ExpenseStatusBadge status={e.status} />
                        </td>
                        {/* Exhaustive map, absent is a dash. A company-card
                            receipt reading as "Personal" is a reimbursement
                            nobody is owed. */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          {tab === 'pending' && perms.canCorrectPaidBy ? (
                            // Correctable right up to approval — a hand may
                            // file 'company' and the office is who knows for
                            // certain. Editable ONLY here: the API's PATCH
                            // 409s on any field once the row is past pending.
                            // Its own gate, not approve's: PATCH carries no
                            // requireRole, so a plain dispatcher may correct
                            // this while being refused Approve two cells over.
                            <select
                              aria-label={`Who paid — ${e.vendor ?? 'expense'}`}
                              value={e.paidBy ?? ''}
                              disabled={paidByBusyId === e.id}
                              onChange={(ev) => {
                                const next = toPaidBy(ev.target.value);
                                // A value outside the pair is not a correction
                                // to send — the API 400s on it — and an
                                // unchanged value is not a write at all.
                                if (!next || next === e.paidBy) return;
                                paidByMutation.mutate({ id: e.id, paidBy: next, was: e.paidBy });
                              }}
                              className="rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-2 py-1 text-xs text-fieldiq-text focus:border-fi-amber outline-none disabled:opacity-50"
                            >
                              {/* A select cannot show a value it has no option
                                  for — it shows the FIRST one while the row
                                  still holds the real one. An absent or
                                  unrecognised paid_by therefore gets its own
                                  option rather than silently reading as
                                  "Personal". */}
                              {toPaidBy(e.paidBy) === null && (
                                <option value={e.paidBy ?? ''}>{getPaidByLabel(e.paidBy)}</option>
                              )}
                              {PAID_BY_VALUES.map((v) => (
                                <option key={v} value={v}>{PAID_BY_LABELS[v]}</option>
                              ))}
                            </select>
                          ) : (
                            <span className={e.paidBy === 'company' ? 'text-fieldiq-text' : 'text-fieldiq-muted'}>
                              {getPaidByLabel(e.paidBy)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-extrabold text-fieldiq-text tabular-nums whitespace-nowrap">{fmtCurrency(e.amount)}</td>
                        <td className="px-4 py-3 text-right text-fieldiq-muted tabular-nums whitespace-nowrap">{fmtCurrency(e.gst)}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <ReceiptViewer uploadId={e.receiptUploadId} />
                        </td>
                        {tab === 'approved' && (
                          <td className="px-4 py-3 text-fieldiq-text whitespace-nowrap">
                            {fmtPayPeriod(e.payPeriod)}
                            {e.carriedForward && (
                              <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-fi-amber/15 text-fi-signal-amber">
                                carried forward
                              </span>
                            )}
                          </td>
                        )}
                        {tab === 'approved' && (
                          <td className="px-4 py-3 whitespace-nowrap" title={e.periodTotalNote ?? undefined}>
                            {/* THE COMPANY-PAID CASE IS CHECKED FIRST, and on
                                the API's word rather than on the nulls. A
                                company-paid row's reflected flag is null — the
                                same null a failed approval leaves — so reading
                                the flag alone would print "—" over a perfectly
                                correct result and call it missing. */}
                            {e.payrollPath === 'none' ? (
                              <span className="text-fieldiq-muted">{getPayrollPathLabel(e.payrollPath)}</span>
                            ) : e.reimbursementPath === 'report' ? (
                              /* FE-5: the reimbursement report, not a pay period —
                                 owed until the office marks it paid back. The
                                 day is the TENANT's day of the mark. */
                              <span data-testid={`expense-path-${e.id}`}
                                className={e.reimbursedAt ? 'text-fi-signal-green' : 'text-fi-signal-amber'}>
                                {reimbursementPathLabel('report', !!e.reimbursedAt, instantTenantDay(e.reimbursedAt, tenantZone))}
                              </span>
                            ) : e.reflectedInPeriodTotal === true ? (
                              <span className="text-fi-signal-green">In period total</span>
                            ) : e.reflectedInPeriodTotal === false ? (
                              <span className="text-fi-signal-amber">Not yet</span>
                            ) : (
                              <span className="text-fieldiq-muted">—</span>
                            )}
                          </td>
                        )}
                        {tab === 'rejected' && (
                          <td className="px-4 py-3 text-fi-signal-red">{e.rejectionReason ?? '—'}</td>
                        )}
                        {tab === 'pending' && (perms.canApprove || perms.canReject) && (
                          <td className="px-4 py-3 text-right whitespace-nowrap sticky right-0 bg-fieldiq-card">
                            {perms.canApprove && destinationOf(e) && (
                              <p data-testid={`approve-destination-${e.id}`} className="mb-1 text-[11px] text-fieldiq-muted">
                                Approving sends it to: <span className="font-bold text-fieldiq-text">{destinationOf(e)}</span>
                              </p>
                            )}
                            <div className="inline-flex items-center gap-2">
                              {/* Two routes, two gates, two controls. They
                                  agree today and are read separately so that
                                  one moving cannot silently move the other.

                                  THESE GATES ARE DUPLICATED, DELIBERATELY AND
                                  MEASURABLY. The cell above is gated on the
                                  same permissions, because the header `<th>`
                                  is and the two must agree or the columns
                                  misalign. Deleting either copy reddens ZERO
                                  of this screen's tests; deleting both reddens
                                  two — the fe#599 shape, where a revert that
                                  reddens nothing means "duplicated", not
                                  "untested". The cell gate is load-bearing, so
                                  the pair stands; the attribution lives in
                                  useExpensePermissions.test.tsx, which pins
                                  each flag where nothing else can cover it. */}
                              {perms.canApprove && (
                                <button
                                  onClick={() =>
                                    approveMutation.mutate({
                                      id: e.id,
                                      ticketId: editedTicket(e),
                                      changed: editedTicket(e) !== (e.ticketId ?? null),
                                    })
                                  }
                                  disabled={approveMutation.isPending}
                                  className="fi-action fi-action--compact fi-action--primary disabled:opacity-50"
                                >
                                  <Check size={14} /> Approve
                                </button>
                              )}
                              {perms.canReject && (
                                <button
                                  onClick={() => { rejectMutation.reset(); setRejectTarget(e); }}
                                  className="fi-action fi-action--compact border border-fieldiq-border bg-fieldiq-card text-fieldiq-text"
                                >
                                  <X size={14} /> Reject
                                </button>
                              )}
                            </div>
                            {approveErrors[e.id] && (
                              <p role="alert" data-testid={`approve-error-${e.id}`}
                                className="mt-1 max-w-[18rem] whitespace-normal text-left text-[11px] font-medium text-fi-signal-red">
                                {approveErrors[e.id]}
                              </p>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <footer className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
              The figures above describe the receipts in view, not every receipt on file.
            </footer>
          </section>

          {/* FE-2: approved cost, from GET /expenses/summary. Office view roles
              only — the endpoint 403s everyone else, and a filer gains nothing
              from a company-wide cost table. */}
          {officeView && <ExpenseCostSummary />}
          </>)}
        </div>
      </section>

      {rejectTarget && (
        <RejectModal
          expense={rejectTarget}
          isLoading={rejectMutation.isPending}
          error={rejectMutation.isError ? getApiErrorMessage(rejectMutation.error, 'Could not reject this expense.') : null}
          onClose={() => setRejectTarget(null)}
          onConfirm={(reason) => rejectMutation.mutate({ id: rejectTarget.id, reason })}
        />
      )}
    </div>
  );
}
